/*
 * Vajihi Labs -- Merge Forward
 * Free tier: limited to 3 emails per forward.
 * Pro tier: license key bound to the signed-in mailbox, validated against
 * the Vajihi Labs licensing API.
 */

/* global document, Office */

const FREE_TIER_EMAIL_LIMIT = 3;

const LICENSE_API_URL = "https://vajihi-labs.lovable.app/api/public/validate-license";
// Switch to "https://vajihilabs.com/api/public/validate-license" once the custom domain is connected.

const ROAMING_KEY_LICENSE_KEY = "vf_license_key";
const ROAMING_KEY_LICENSE_STATE = "vf_license_state"; // "active" | "inactive"
const ROAMING_KEY_LAST_VALIDATED = "vf_license_last_validated"; // ISO timestamp

const REVALIDATE_INTERVAL_MS = 24 * 60 * 60 * 1000; // once a day

let lastSelectedItems: Office.SelectedItemDetails[] = [];
let isForwarding = false;
let isProActive = false;

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    document.getElementById("sideload-msg")!.style.display = "none";
    document.getElementById("app-body")!.style.display = "block";
    document.getElementById("run")!.onclick = runOutlook;
    document.getElementById("forward")!.onclick = handleForward;
    document.getElementById("activate-license")!.onclick = handleActivateLicense;

    const radios = document.getElementsByName("forward-mode") as NodeListOf<HTMLInputElement>;
    radios.forEach((radio) => {
      radio.addEventListener("change", () => {
        updateSummaryAndButton(lastSelectedItems.length);
      });
    });

    Office.context.mailbox.addHandlerAsync(
      Office.EventType.SelectedItemsChanged,
      runOutlook,
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.log(asyncResult.error.message);
        }
      }
    );

    initializeLicenseState();
    runOutlook();
  }
});

/* ---------------- License handling ---------------- */

function getMailboxEmail(): string {
  return Office.context.mailbox.userProfile.emailAddress;
}

function roamingGet(key: string): string | undefined {
  return Office.context.roamingSettings.get(key);
}

function roamingSet(key: string, value: string) {
  Office.context.roamingSettings.set(key, value);
}

function roamingSaveAsync(): Promise<void> {
  return new Promise((resolve) => {
    Office.context.roamingSettings.saveAsync(() => resolve());
  });
}

interface ValidateLicenseResponse {
  valid: boolean;
  product?: string;
  status?: string;
  error?: string;
}

function callValidateLicense(licenseKey: string, email: string): Promise<{ ok: true; data: ValidateLicenseResponse } | { ok: false }> {
  return fetch(LICENSE_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ license_key: licenseKey, email: email }),
  })
    .then((res) => res.json())
    .then((data: ValidateLicenseResponse) => ({ ok: true as const, data }))
    .catch(() => ({ ok: false as const }));
}

function setProUiState(active: boolean) {
  isProActive = active;
  const badge = document.getElementById("pro-badge")!;
  const activateSection = document.getElementById("license-activate-section")!;
  const activeSection = document.getElementById("license-active-section")!;

  badge.style.display = active ? "inline-block" : "none";
  activateSection.style.display = active ? "none" : "block";
  activeSection.style.display = active ? "block" : "none";
}

// Runs on every add-in load: uses the cached state immediately (no network
// call on every action), then re-validates in the background at most once
// per REVALIDATE_INTERVAL_MS.
async function initializeLicenseState() {
  const cachedState = roamingGet(ROAMING_KEY_LICENSE_STATE);
  const cachedKey = roamingGet(ROAMING_KEY_LICENSE_KEY);

  if (cachedState === "active" && cachedKey) {
    setProUiState(true);
  } else {
    setProUiState(false);
  }

  if (!cachedKey) {
    return; // never activated, nothing to revalidate
  }

  const lastValidatedRaw = roamingGet(ROAMING_KEY_LAST_VALIDATED);
  const lastValidated = lastValidatedRaw ? new Date(lastValidatedRaw).getTime() : 0;
  const dueForRevalidation = Date.now() - lastValidated > REVALIDATE_INTERVAL_MS;

  if (!dueForRevalidation) {
    return;
  }

  const result = await callValidateLicense(cachedKey, getMailboxEmail());

  if (!result.ok) {
    // Transient network failure -- do not downgrade an already-cached Pro user.
    return;
  }

  const data = result.data;

  if (data.valid) {
    roamingSet(ROAMING_KEY_LICENSE_STATE, "active");
    roamingSet(ROAMING_KEY_LAST_VALIDATED, new Date().toISOString());
    await roamingSaveAsync();
    setProUiState(true);
    return;
  }

  if (data.error === "rate_limited" || data.error === "temporarily_unavailable") {
    // Transient -- do not downgrade, just skip updating last-validated so we retry soon.
    return;
  }

  // Genuinely invalid, revoked, or bound elsewhere on background re-check --
  // silently fall back to free tier per spec.
  roamingSet(ROAMING_KEY_LICENSE_STATE, "inactive");
  roamingSet(ROAMING_KEY_LAST_VALIDATED, new Date().toISOString());
  await roamingSaveAsync();
  setProUiState(false);
}

async function handleActivateLicense() {
  const input = document.getElementById("license-key-input") as HTMLInputElement;
  const statusEl = document.getElementById("license-status")!;
  const button = document.getElementById("activate-license") as HTMLElement;

  const licenseKey = input.value.trim();
  if (!licenseKey) {
    statusEl.textContent = "Enter a license key first.";
    statusEl.classList.add("is-error");
    return;
  }

  statusEl.classList.remove("is-error");
  statusEl.textContent = "Verifying...";
  button.style.pointerEvents = "none";

  const email = getMailboxEmail();
  const result = await callValidateLicense(licenseKey, email);

  button.style.pointerEvents = "auto";

  if (!result.ok) {
    statusEl.textContent = "Couldn't verify right now, try again shortly.";
    statusEl.classList.add("is-error");
    return;
  }

  const data = result.data;

  if (data.valid) {
    roamingSet(ROAMING_KEY_LICENSE_KEY, licenseKey);
    roamingSet(ROAMING_KEY_LICENSE_STATE, "active");
    roamingSet(ROAMING_KEY_LAST_VALIDATED, new Date().toISOString());
    await roamingSaveAsync();
    statusEl.textContent = "";
    statusEl.classList.remove("is-error");
    setProUiState(true);
    return;
  }

  if (data.error === "license_bound_elsewhere") {
    statusEl.textContent =
      "This license is already active on a different mailbox. If this is your license and you need to move it, contact support@vajihilabs.com.";
    statusEl.classList.add("is-error");
    return;
  }

  if (data.error === "rate_limited" || data.error === "temporarily_unavailable") {
    statusEl.textContent = "Couldn't verify right now, try again shortly.";
    statusEl.classList.add("is-error");
    return;
  }

  // "invalid_request", or valid:false with no error field.
  statusEl.textContent = "That license key isn't valid.";
  statusEl.classList.add("is-error");
}

/* ---------------- Selection + forwarding (unchanged behavior) ---------------- */

function getSelectedItems(): Promise<Office.SelectedItemDetails[]> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.getSelectedItemsAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        reject(asyncResult.error.message);
        return;
      }
      resolve(asyncResult.value as Office.SelectedItemDetails[]);
    });
  });
}

function getSelectedForwardMode(): "attach" | "merge" {
  const radios = document.getElementsByName("forward-mode") as NodeListOf<HTMLInputElement>;
  for (let i = 0; i < radios.length; i++) {
    if (radios[i].checked) {
      return radios[i].value as "attach" | "merge";
    }
  }
  return "attach";
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderMailbox(items: Office.SelectedItemDetails[]) {
  const listEl = document.getElementById("selected-items")!;
  const countEl = document.getElementById("selected-count")!;

  countEl.textContent = String(items.length);

  if (items.length === 0) {
    listEl.innerHTML = '<div class="vf-empty">No emails selected. Hold Ctrl (or Cmd) and click emails, then Refresh.</div>';
    return;
  }

  let html = "";
  items.forEach((item: any) => {
    const subject = item.subject || "(no subject)";
    html += '<div class="vf-mail">';
    html += '<span class="vf-avatar" aria-hidden="true">&#9993;</span>';
    html += '<div class="vf-mail-text"><div class="vf-subject">' + escapeHtml(subject) + "</div></div>";
    html += '<span class="vf-tick" aria-label="Selected">&#10003;</span>';
    html += "</div>";
  });
  listEl.innerHTML = html;
}

function updateSummaryAndButton(count: number) {
  const summaryEl = document.getElementById("forward-summary")!;
  const buttonEl = document.getElementById("forward") as HTMLButtonElement;
  const mode = getSelectedForwardMode();

  const effectiveLimit = isProActive ? Infinity : FREE_TIER_EMAIL_LIMIT;

  if (count === 0) {
    summaryEl.textContent = isProActive
      ? "Select emails to forward."
      : "Select up to " + FREE_TIER_EMAIL_LIMIT + " emails to forward.";
    buttonEl.disabled = true;
    buttonEl.textContent = "Forward";
  } else if (count > effectiveLimit) {
    summaryEl.textContent =
      "Free version supports up to " + FREE_TIER_EMAIL_LIMIT + " emails -- you selected " + count + ".";
    buttonEl.disabled = true;
    buttonEl.textContent = "Forward";
  } else {
    summaryEl.textContent =
      mode === "merge"
        ? "1 combined body + " + count + " original attachment" + (count === 1 ? "" : "s")
        : count + " email" + (count === 1 ? "" : "s") + " as separate attachments";
    buttonEl.disabled = isForwarding;
    buttonEl.textContent = "Forward " + count + " email" + (count === 1 ? "" : "s");
  }
}

export async function runOutlook() {
  const statusEl = document.getElementById("forward-status")!;
  statusEl.textContent = "";
  statusEl.classList.remove("is-error");

  try {
    const items = await getSelectedItems();
    lastSelectedItems = items || [];
    renderMailbox(lastSelectedItems);
    updateSummaryAndButton(lastSelectedItems.length);
  } catch (err) {
    lastSelectedItems = [];
    renderMailbox([]);
    updateSummaryAndButton(0);
    statusEl.textContent = "Error refreshing selection: " + err;
    statusEl.classList.add("is-error");
  }
}

function loadItemBody(itemId: string): Promise<{ subject: string; from: string; text: string }> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.loadItemByIdAsync(itemId, (loadResult) => {
      if (loadResult.status === Office.AsyncResultStatus.Failed) {
        reject(loadResult.error.message);
        return;
      }
      const loadedItem: any = loadResult.value;
      loadedItem.body.getAsync(Office.CoercionType.Text, (bodyResult: any) => {
        const text = bodyResult.status === Office.AsyncResultStatus.Succeeded ? bodyResult.value : "(could not load body)";
        const subject = loadedItem.subject || "(no subject)";
        const from = loadedItem.from ? loadedItem.from.emailAddress : "(unknown sender)";
        loadedItem.unloadAsync(() => {
          resolve({ subject, from, text });
        });
      });
    });
  });
}

const MAX_HTML_BODY_CHARS = 28000;
const PER_EMAIL_CHAR_LIMIT = 4000;

export async function handleForward() {
  if (isForwarding) return;

  const statusEl = document.getElementById("forward-status")!;
  const buttonEl = document.getElementById("forward") as HTMLButtonElement;
  statusEl.classList.remove("is-error");

  const items = lastSelectedItems;
  if (!items || items.length === 0) {
    statusEl.textContent = "No messages selected.";
    statusEl.classList.add("is-error");
    return;
  }

  const effectiveLimit = isProActive ? Infinity : FREE_TIER_EMAIL_LIMIT;
  if (items.length > effectiveLimit) {
    statusEl.textContent =
      "Free version supports up to " + FREE_TIER_EMAIL_LIMIT + " emails at a time. Please select " +
      FREE_TIER_EMAIL_LIMIT + " or fewer, or activate a Pro license.";
    statusEl.classList.add("is-error");
    return;
  }

  isForwarding = true;
  buttonEl.disabled = true;
  const previousLabel = buttonEl.textContent;
  buttonEl.textContent = "Forwarding...";
  statusEl.textContent = "Preparing...";

  try {
    const mode = getSelectedForwardMode();

    const itemAttachments = items.map((item: any) => ({
      type: Office.MailboxEnums.AttachmentType.Item,
      itemId: item.itemId,
      name: (item.subject || "Email") + ".eml",
    }));

    let htmlBody = "";
    let truncatedAny = false;

    if (mode === "merge") {
      for (const item of items) {
        const { subject, from, text } = await loadItemBody((item as any).itemId);
        let bodyText = text;
        if (bodyText.length > PER_EMAIL_CHAR_LIMIT) {
          bodyText = bodyText.slice(0, PER_EMAIL_CHAR_LIMIT) + "\n...[truncated]...";
          truncatedAny = true;
        }
        const escapedBody = bodyText.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
        htmlBody += "<hr/><p><b>From:</b> " + from + "<br/><b>Subject:</b> " + subject + "</p><p>" + escapedBody + "</p>";

        if (htmlBody.length > MAX_HTML_BODY_CHARS) {
          htmlBody = htmlBody.slice(0, MAX_HTML_BODY_CHARS) + "<p>...[merge truncated -- too many/large messages]...</p>";
          truncatedAny = true;
          break;
        }
      }
    } else {
      htmlBody = "<p>" + items.length + " email(s) attached.</p>";
    }

    if (mode === "merge" && !isProActive) {
      htmlBody += '<p style="color: #888888; font-size: 11px; margin-top: 20px;">Merged with <a href="https://www.vajihilabs.com" style="color: #888888;">Vajihi Labs \u2014 Merge Forward</a> (free)</p>';
    }

    statusEl.textContent = "Opening compose window...";

    Office.context.mailbox.displayNewMessageFormAsync(
      {
        subject: (mode === "merge" ? "Merged: " : "Fwd: ") + items.length + " messages",
        htmlBody: htmlBody,
        attachments: itemAttachments,
      },
      (result) => {
        isForwarding = false;
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          statusEl.textContent = truncatedAny
            ? "Done (some text truncated to fit size limits) -- check the new compose window."
            : "Done -- check the new compose window.";
          statusEl.classList.remove("is-error");
        } else {
          statusEl.textContent = "Error: " + result.error?.message;
          statusEl.classList.add("is-error");
        }
        updateSummaryAndButton(lastSelectedItems.length);
      }
    );
  } catch (err) {
    isForwarding = false;
    statusEl.textContent = "Error: " + err;
    statusEl.classList.add("is-error");
    buttonEl.textContent = previousLabel;
    updateSummaryAndButton(lastSelectedItems.length);
  }
}
